import { Fragment, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../features/auth/core/useAuth';
import { useUiSettings } from '../../features/config/core/UiSettingsContext';
import { PartnerSelector } from '../../features/partner/navigation/PartnerSelector';
import { PartnerSelectorButton } from '../../features/partner/navigation/PartnerSelectorButton';
import { dashboardNavItems } from './DashboardNavItems';
import { dashboardNavRenderers } from './DashboardNavRenderers';

/* menu sections; nav items pick one via their `group` property. The
   personal section (user page, later the configuration, plus the
   logout action) always comes last. */
const PERSONAL_GROUP = 'personal';
const navGroups = [
  { id: 'administration', labelKey: 'nav.groups.administration' },
  { id: 'hosting', labelKey: 'nav.groups.hosting' },
  { id: PERSONAL_GROUP, labelKey: 'nav.groups.personal' },
];

/*
 * Reusability:
 * We can use the sidebar in other components (like a mobile drawer)
 * without rewriting logic.
 */
export function SidebarLayout() {
  const { t } = useTranslation();
  /* on narrow (phone) screens the sidebar starts collapsed so the
     content gets the width; jsdom has no matchMedia, hence the guard */
  const [isOpen, setIsOpen] = useState(
    () => !window.matchMedia?.('(max-width: 768px)').matches
  );
  const { logout } = useAuth();
  const { showExampleData, showHosting } = useUiSettings();
  const navigate = useNavigate();

  /* the hosting area is hidden until the user opts in on the
     configuration page — either to see the example data or to work with
     the real backend data */
  const visibleGroups = navGroups.filter(
    (group) => group.id !== 'hosting' || showExampleData || showHosting
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /* on phones the open sidebar overlays the content, so it folds away
     as soon as anything gets selected — a menu link (also those from
     custom nav renderers, hence delegated) or a partner in the
     selector; on wide screens it just stays open */
  const closeOnNarrowScreens = (event) => {
    if (!event.target.closest('a, .partner-selector-option')) return;
    if (window.matchMedia?.('(max-width: 768px)').matches) {
      setIsOpen(false);
    }
  };

  /* a custom className function must re-add the `active` class that a
     plain NavLink applies by itself */
  const navLinkClassName =
    (item) =>
    ({ isActive }) =>
      [
        /* the marker that generates the icon box; the item class only
           picks which icon goes into it. Links from a custom renderer
           carry neither and stay plain — an icon box without an icon
           would show up as a filled square. */
        item.iconClass ? 'nav-icon' : null,
        item.iconClass,
        isActive ? 'active' : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined;

  const itemLabel = (item) => (item.labelKey ? t(item.labelKey) : item.label);

  const renderNavItem = (item) => {
    const CustomRenderer = dashboardNavRenderers[item.type];

    if (CustomRenderer) {
      return <CustomRenderer key={item.type} item={item} />;
    }

    return (
      <NavLink key={item.to} to={item.to} className={navLinkClassName(item)}>
        {itemLabel(item)}
        <br />
      </NavLink>
    );
  };

  /* collapsed the sidebar is still wide enough for the icons, so the
     areas stay reachable — and visible — without opening the menu.
     The label becomes the tooltip and the accessible name; the icon
     itself comes from the item's iconClass (see theme.css). Items
     rendered by a custom renderer (the projects tree) have no route of
     their own and therefore no rail entry. */
  const railItems = (group) =>
    dashboardNavItems.filter((item) => item.to && item.group === group);

  const renderRailItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={navLinkClassName(item)}
      title={itemLabel(item)}
      aria-label={itemLabel(item)}
    />
  );

  return (
    <div className={isOpen ? 'app-sidebar is-open' : 'app-sidebar'}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
        aria-label={isOpen ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
      >
        {isOpen ? '«' : '»'}
      </button>

      {!isOpen && (
        <nav className="nav-rail">
          {/* the partner choice heads the rail as it heads the menu:
              it decides what every entry below it shows */}
          <PartnerSelectorButton />
          {railItems(undefined).map(renderRailItem)}
          {visibleGroups.map((group) => {
            const items = railItems(group.id);
            const withLogout = group.id === PERSONAL_GROUP;
            if (items.length === 0 && !withLogout) return null;
            return (
              <Fragment key={group.id}>
                {/* the group headings do not fit the rail; a rule
                    keeps the sections apart instead */}
                <hr />
                {items.map(renderRailItem)}
                {withLogout && (
                  <button
                    onClick={handleLogout}
                    title={t('sidebar.logout')}
                    aria-label={t('sidebar.logout')}
                  />
                )}
              </Fragment>
            );
          })}
        </nav>
      )}

      {isOpen && (
        <nav onClick={closeOnNarrowScreens}>
          <PartnerSelector />
          {/* ungrouped items (e.g. the overview) come before the groups */}
          {dashboardNavItems.filter((item) => !item.group).map(renderNavItem)}
          {visibleGroups.map((group) => {
            const items = dashboardNavItems.filter(
              (item) => item.group === group.id
            );
            /* the logout action lives in the personal section, which
               therefore renders even without nav items */
            const withLogout = group.id === PERSONAL_GROUP;
            if (items.length === 0 && !withLogout) return null;
            return (
              <Fragment key={group.id}>
                <p className="nav-group">{t(group.labelKey)}</p>
                {items.map(renderNavItem)}
                {withLogout && (
                  <p className="nav-logout">
                    <button onClick={handleLogout}>
                      {t('sidebar.logout')}
                    </button>
                  </p>
                )}
              </Fragment>
            );
          })}
        </nav>
      )}
    </div>
  );
}
